import { Capacitor, registerPlugin } from "@capacitor/core";
import type { Schedule } from "./types";

export type AndroidCalendar = {
  id: string;
  name: string;
  account_name?: string;
};

type AndroidSchedulePlugin = {
  listWritableCalendars(): Promise<{ calendars: AndroidCalendar[] }>;
  upsertCalendarEvent(payload: Record<string, unknown>): Promise<{ android_calendar_event_id?: string }>;
  deleteCalendarEvent(payload: { android_calendar_event_id: string }): Promise<{ ok: boolean }>;
  setClockAlarm(payload: Record<string, unknown>): Promise<{ ok: boolean }>;
};

const SELECTED_CALENDAR_KEY = "mindback.androidCalendarId";
const ALARMED_SCHEDULES_KEY = "mindback.androidAlarms";

const AndroidSchedule = registerPlugin<AndroidSchedulePlugin>("AndroidSchedule");

export function getSelectedCalendarId() {
  return localStorage.getItem(SELECTED_CALENDAR_KEY) || "";
}

export function setSelectedCalendarId(value: string) {
  if (value) localStorage.setItem(SELECTED_CALENDAR_KEY, value);
  else localStorage.removeItem(SELECTED_CALENDAR_KEY);
}

export async function listWritableAndroidCalendars() {
  if (!Capacitor.isNativePlatform()) return [] as AndroidCalendar[];
  const response = await AndroidSchedule.listWritableCalendars();
  return response.calendars || [];
}

export async function syncAndroidSchedules(schedules: Schedule[]) {
  if (!Capacitor.isNativePlatform()) return [];
  const calendarId = getSelectedCalendarId();
  const updates: Array<{ id: string; android_calendar_event_id?: string }> = [];
  for (const schedule of schedules) {
    if (schedule.status === "deleted" || schedule.status === "canceled") {
      if (schedule.android_calendar_event_id) {
        await AndroidSchedule.deleteCalendarEvent({ android_calendar_event_id: schedule.android_calendar_event_id }).catch(() => undefined);
      }
      continue;
    }
    if (schedule.status !== "scheduled" || !calendarId) continue;
    const response: { android_calendar_event_id?: string } = await AndroidSchedule.upsertCalendarEvent({
      calendar_id: calendarId,
      android_calendar_event_id: schedule.android_calendar_event_id || "",
      title: schedule.title,
      start_at: schedule.start_at,
      end_at: schedule.end_at || "",
      all_day: schedule.all_day,
      remind_minutes_before: schedule.remind_minutes_before ?? null
    }).catch(() => ({}));
    if (response.android_calendar_event_id && response.android_calendar_event_id !== schedule.android_calendar_event_id) {
      updates.push({ id: schedule.id, android_calendar_event_id: response.android_calendar_event_id });
    }
    if (schedule.alarm_enabled && shouldCreateClockAlarm(schedule)) {
      await AndroidSchedule.setClockAlarm({
        schedule_id: schedule.id,
        title: schedule.title,
        start_at: schedule.start_at
      }).then(() => markClockAlarmCreated(schedule)).catch(() => undefined);
    }
  }
  return updates;
}

function shouldCreateClockAlarm(schedule: Schedule) {
  const alarmed = readAlarmedSchedules();
  return alarmed[schedule.id] !== alarmSignature(schedule);
}

function markClockAlarmCreated(schedule: Schedule) {
  const alarmed = readAlarmedSchedules();
  alarmed[schedule.id] = alarmSignature(schedule);
  localStorage.setItem(ALARMED_SCHEDULES_KEY, JSON.stringify(alarmed));
}

function alarmSignature(schedule: Schedule) {
  return [schedule.title, schedule.start_at, schedule.end_at || "", schedule.alarm_enabled ? "1" : "0"].join("|");
}

function readAlarmedSchedules(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(ALARMED_SCHEDULES_KEY) || "{}");
  } catch {
    return {};
  }
}
